import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from "../utils/debounce.js";
const defaultIgnoreDimensions = [];
const defaultInitialSize = {
  width: 0,
  height: 0,
  top: 0,
  left: 0
};
export default function useParentSize() {
  let {
    initialSize = defaultInitialSize,
    debounceTime = 300,
    ignoreDimensions = defaultIgnoreDimensions,
    enableDebounceLeadingCall = true,
    resizeObserverPolyfill,
    externalRef
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const [node, setNode] = useState(null);
  const parentRef = useCallback(el => {
    setNode(el);
    if (typeof externalRef === 'function') {
      externalRef(el);
    } else if (externalRef != null) {
      // eslint-disable-next-line no-param-reassign -- standard ref-merging pattern
      externalRef.current = el;
    }
  }, [externalRef]);
  const animationFrameID = useRef(0);
  const [state, setState] = useState({
    ...defaultInitialSize,
    ...initialSize
  });
  const resize = useMemo(() => {
    const normalized = Array.isArray(ignoreDimensions) ? ignoreDimensions : [ignoreDimensions];
    return debounce(incoming => {
      setState(existing => {
        const stateKeys = Object.keys(existing);
        const keysWithChanges = stateKeys.filter(key => existing[key] !== incoming[key]);
        const shouldBail = keysWithChanges.every(key => normalized.includes(key));
        return shouldBail ? existing : incoming;
      });
    }, debounceTime, {
      leading: enableDebounceLeadingCall
    });
  }, [debounceTime, enableDebounceLeadingCall, ignoreDimensions]);
  useEffect(() => {
    if (!node) {
      resize.cancel();
      return;
    }
    const LocalResizeObserver = resizeObserverPolyfill || window.ResizeObserver;
    const observer = new LocalResizeObserver(entries => {
      entries.forEach(entry => {
        const {
          left,
          top,
          width,
          height
        } = entry?.contentRect ?? {};
        animationFrameID.current = window.requestAnimationFrame(() => {
          resize({
            width,
            height,
            top,
            left
          });
        });
      });
    });
    observer.observe(node);
    return () => {
      window.cancelAnimationFrame(animationFrameID.current);
      observer.disconnect();
      resize.cancel();
    };
  }, [node, resize, resizeObserverPolyfill]);
  return {
    parentRef,
    node,
    resize,
    ...state
  };
}